import React, { FC, ReactElement } from "react";
import { useLocation } from "react-router-dom";

import { Box, Drawer, Stack } from "@mui/material";

import { Scrollbar } from "../../components/scrollbar";
import { Item, useMenuItems } from "../../hooks/use-menu-items";
import { MobileNavItem } from "./mobile-nav-item";

const MOBILE_NAV_WIDTH = 280;

const renderItems = ({
  depth = 0,
  items,
  pathname,
}: {
  depth?: number;
  items: Item[];
  pathname?: string | null;
}): ReactElement[] =>
  items.reduce(
    (acc: ReactElement[], item) =>
      reduceChildRoutes({
        acc,
        depth,
        item,
        pathname,
      }),
    [],
  );

const reduceChildRoutes = ({
  acc,
  depth,
  item,
  pathname,
}: {
  acc: ReactElement[];
  depth: number;
  item: Item;
  pathname?: string | null;
}): Array<ReactElement> => {
  const checkPath = !!(item.path && pathname);
  const partialMatch = checkPath ? pathname.includes(item.path!) : false;
  const exactMatch = checkPath ? pathname === item.path : false;
  const openState = localStorage.getItem("SideNavOpen_" + item.title);
  const startOpen = !(openState === "false");

  if (item.items) {
    acc.push(
      <MobileNavItem
        active={partialMatch}
        depth={depth}
        external={item.external}
        icon={item.icon}
        key={item.title}
        openImmediately={partialMatch || startOpen}
        path={item.path}
        title={item.title}
      >
        <Stack
          component="ul"
          spacing={0.5}
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
          }}
        >
          {renderItems({
            depth: depth + 1,
            items: item.items,
            pathname,
          })}
        </Stack>
      </MobileNavItem>,
    );
  } else {
    acc.push(
      <MobileNavItem
        active={exactMatch}
        depth={depth}
        external={item.external}
        icon={item.icon}
        key={item.title}
        path={item.path}
        title={item.title}
      />,
    );
  }

  return acc;
};

interface MobileNavProps {
  onClose?: () => void;
  open?: boolean;
}

export const MobileNav: FC<MobileNavProps> = (props) => {
  const { open, onClose } = props;
  const { pathname } = useLocation();
  const items = useMenuItems();

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          width: MOBILE_NAV_WIDTH,
        },
      }}
      variant="temporary"
    >
      <Scrollbar
        sx={{
          height: "100%",
          "& .simplebar-content": {
            height: "100%",
          },
        }}
      >
        <Box
          component="nav"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            p: 2,
          }}
        >
          <Box
            component="ul"
            sx={{
              flexGrow: 1,
              listStyle: "none",
              m: 0,
              p: 0,
            }}
          >
            {renderItems({
              depth: 0,
              items,
              pathname,
            })}
          </Box>
        </Box>
      </Scrollbar>
    </Drawer>
  );
};
